// The shape of the `apiFetch` each app injects into shared code.
//
// We deliberately do NOT share apiFetch itself: it depends on the platform's
// token storage (mobile's AsyncStorage vs web's localStorage), which is the
// storage-adapter work deferred in next/rnw-strangler.md §4. What IS shared is
// the *contract* — so the ~65 injection points across packages/ui and
// packages/shared stop re-declaring it by hand.
//
// There are three contracts here, not one. Collapsing them into a single type
// would advertise capabilities to consumers that don't honour them: a Progress
// panel must not be able to pass `onEtag` to a fetcher that never fires it.
// Hence a base plus a parameter, rather than one flat option bag.
//
// Assignability note: a concrete apiFetch whose options carry EXTRA optional
// fields is assignable to a narrower alias here — passing the narrow shape
// always satisfies the wide one. Both real implementations
// (apps/mobile/src/api/index.ts, apps/web-rnw/src/api.ts) accept a superset,
// and every alias below relies on that.

/** The HTTP verbs both apps' option bags accept. Widening this to `string`
 *  breaks the callsite contravariance check. */
export type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Path in, parsed JSON out. The read-only contract, for helpers that only
 *  ever GET and so should not be handed a way to write. */
export type ApiGet = <T = unknown>(path: string) => Promise<T>;

/** The option bag every writing fetcher accepts. */
export type ApiFetchOptions = {
  method?: ApiMethod;
  body?: unknown;
};

/**
 * A fetcher that can write, optionally extended with surface-specific
 * options. `O` is what a given surface needs ON TOP of method/body — keep the
 * rationale for those extras at the alias site, not here, so an unrelated
 * surface instantiating this doesn't inherit someone else's semantics.
 */
export type ApiFetchWith<O = unknown> = <T = unknown>(
  path: string,
  opts?: ApiFetchOptions & O,
) => Promise<T>;
